<template>
   <div class="w-full sm:w-1/2 lg:w-1/4 px-2 mb-4">
      <div class="kpi-card flex flex-col justify-between h-full" :class="{ 'kpi-card--active': showInfo }">
         <div>
            <div class="flex items-center justify-between mb-2">
               <div class="flex items-center gap-2">
                  <span class="kpi-card__tag">{{ kpi.kpiTag }}</span>
                  <div ref="info" class="kpi-card__info">
                     <button type="button" class="kpi-card__info-button" @click.stop="toggleInfo"
                        :aria-expanded="showInfo ? 'true' : 'false'"
                        :title="'¿Qué significa ' + kpi.title + '?'" aria-label="Más información">
                        <feather-icon icon="InfoIcon" svgClasses="h-4 w-4" />
                     </button>
                     <transition name="kpi-fade">
                        <div v-if="showInfo" class="kpi-card__tooltip" role="tooltip">
                           <strong>{{ kpi.title }}</strong>
                           <p>{{ kpi.description }}</p>
                        </div>
                     </transition>
                  </div>
               </div>
               <div :class="['kpi-card__icon', kpi.badgeBg, kpi.badgeText]">
                  <i :class="['fa-solid', kpi.icon, 'text-sm']"></i>
               </div>
            </div>
            <div class="kpi-card__value">{{ formatNumber(kpi.value) }}</div>
         </div>

         <!-- Botón individual de exportación Excel para el KPI -->
         <div class="kpi-card__footer flex items-center justify-between">
            <span class="kpi-card__subtitle truncate" style="max-width: 120px;" :title="kpi.subtitle">{{ kpi.subtitle
               }}</span>
            <button v-if="kpi.value > 0 && !!kpi.excelFilename" type="button" @click="$emit('export-excel', kpi)"
               class="kpi-card__action inline-flex items-center"
               :title="'Descargar reporte de ' + kpi.title + ' en Excel'">
               <span>Excel</span>
            </button>
         </div>
      </div>
   </div>
</template>

<script>
export default {
   name: 'KpiCard',
   props: {
      kpi: {
         type: Object,
         required: true
      },
      onExportExcel: {
         type: Function,
         required: false
      }
   },
   data() {
      return {
         showInfo: false
      };
   },
   methods: {
      formatNumber(value) {
         return new Intl.NumberFormat().format(value);
      },
      toggleInfo() {
         this.showInfo = !this.showInfo;
      },
      handleClickOutside(event) {
         const info = this.$refs.info;
         if (info && !info.contains(event.target)) {
            this.showInfo = false;
         }
      },
      handleEscape(event) {
         if (event.key === 'Escape') {
            this.showInfo = false;
         }
      }
   },
   mounted() {
      document.addEventListener('click', this.handleClickOutside);
      document.addEventListener('keydown', this.handleEscape);
   },
   beforeDestroy() {
      document.removeEventListener('click', this.handleClickOutside);
      document.removeEventListener('keydown', this.handleEscape);
   }
};
</script>

<style lang="scss" scoped>
.kpi-card {
   background: linear-gradient(180deg, #ffffff 0%, #f6f9ff 100%);
   border: 1px solid rgba(32, 62, 153, 0.18);
   border-radius: 1rem;
   box-shadow: 0 14px 32px rgba(15, 23, 42, 0.08);
   overflow: visible;
   padding: 1rem;
   position: relative;
   transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
   z-index: 1;
}

.kpi-card::before {
   border: 1px solid rgba(255, 255, 255, 0.9);
   border-radius: inherit;
   content: '';
   inset: 0;
   pointer-events: none;
   position: absolute;
}

.kpi-card:hover {
   border-color: rgba(32, 62, 153, 0.28);
   box-shadow: 0 20px 38px rgba(32, 62, 153, 0.14);
   transform: translateY(-2px);
}

.kpi-card:hover,
.kpi-card--active {
   z-index: 20;
}

.kpi-card__tag {
   color: #5d6b8a;
   font-size: 0.72rem;
   font-weight: 700;
   letter-spacing: 0.08em;
   text-transform: uppercase;
}

.kpi-card__icon {
   align-items: center;
   border-radius: 0.9rem;
   box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.45);
   display: inline-flex;
   justify-content: center;
   min-height: 42px;
   min-width: 42px;
}

.kpi-card__info {
   display: inline-flex;
}

.kpi-card__info-button {
   align-items: center;
   background: #eef3ff;
   border: 1px solid rgba(32, 62, 153, 0.18);
   border-radius: 9999px;
   color: #203e99;
   cursor: pointer;
   display: inline-flex;
   font-size: 0.72rem;
   height: 20px;
   justify-content: center;
   transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
   width: 20px;
}

.kpi-card__info-button:hover {
   background: #203e99;
   color: #ffffff;
   box-shadow: 0 6px 14px rgba(32, 62, 153, 0.24);
}

.kpi-card__info-button:focus {
   box-shadow: 0 0 0 3px rgba(32, 62, 153, 0.18);
   outline: none;
}

.kpi-card__tooltip {
   background: #0f172a;
   border-radius: 0.6rem;
   box-shadow: 0 16px 30px rgba(15, 23, 42, 0.25);
   color: #e2e8f0;
   font-size: 0.75rem;
   font-weight: 400;
   left: 0;
   line-height: 1.4;
   padding: 0.65rem 0.8rem;
   position: absolute;
   right: 0;
   top: calc(100% + 8px);
   z-index: 30;
}

.kpi-card__tooltip strong {
   color: #ffffff;
   display: block;
   font-size: 0.78rem;
   margin-bottom: 0.3rem;
}

.kpi-card__tooltip p {
   margin: 0;
}

.kpi-fade-enter-active,
.kpi-fade-leave-active {
   transition: opacity 0.15s ease, transform 0.15s ease;
}

.kpi-fade-enter,
.kpi-fade-leave-to {
   opacity: 0;
   transform: translateY(-4px);
}

.kpi-card__value {
   color: #0f172a;
   font-size: 2rem;
   font-weight: 700;
   line-height: 1.1;
   margin-top: 0.5rem;
}

.kpi-card__footer {
   border-top: 1px solid rgba(148, 163, 184, 0.25);
   margin-top: 1.1rem;
   padding-top: 0.9rem;
}

.kpi-card__subtitle {
   color: #64748b;
   font-size: 0.76rem;
   font-weight: 500;
}

.kpi-card__action {
   align-items: center;
   appearance: none;
   background: linear-gradient(180deg, #f0fff5 0%, #dcfce7 100%);
   border: 1px solid #b7e4c7;
   border-radius: 0.8rem;
   box-shadow: 0 8px 18px rgba(22, 101, 52, 0.08);
   color: #166534;
   cursor: pointer;
   display: inline-flex;
   font-size: 0.76rem;
   font-weight: 700;
   gap: 0.35rem;
   justify-content: center;
   line-height: 1;
   min-height: 36px;
   padding: 0.45rem 0.75rem;
   text-decoration: none;
   transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
   white-space: nowrap;
}

.kpi-card__action:hover {
   background: linear-gradient(180deg, #dcfce7 0%, #bbf7d0 100%);
   box-shadow: 0 12px 22px rgba(22, 101, 52, 0.16);
   transform: translateY(-1px);
}

.kpi-card__action:focus {
   outline: none;
   box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.18);
}
</style>
